import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useEffect } from 'react';

import { Providers } from '@/app/providers';
import { queryClient } from '@/app/query-client';
import { getRouter } from '@/app/router-ref';
import { NotFound } from '@/components/composed/NotFound';
import { RouteError } from '@/components/composed/RouteError';
import { RoutePending } from '@/components/composed/RoutePending';
import { installSessionIsolation } from '@/lib/session/session-isolation';

/*
 * Root route (spec §4.2). Providers + outlet + global error/notFound/pending.
 * Devtools mount only in dev and are tree-shaken from production.
 */
export interface RouterContext {
  queryClient: QueryClient;
}

/**
 * Redirect to /login outside React, mirroring the global 401 handler: use the
 * router when it is registered, else fall back to a hard navigation. Lives here
 * (the app/boot layer, via router-ref) and is injected into the lib-layer
 * session-isolation installer so that module never imports `@/app/*`.
 */
function redirectToLogin(): void {
  const router = getRouter();
  if (router) {
    void router.navigate({ to: '/login' });
  } else {
    window.location.assign('/login');
  }
}

const Devtools = import.meta.env.DEV
  ? lazy(async () => {
      const [{ TanStackRouterDevtools }, { ReactQueryDevtools }] =
        await Promise.all([
          import('@tanstack/react-router-devtools'),
          import('@tanstack/react-query-devtools'),
        ]);
      return {
        default: () => (
          <>
            <TanStackRouterDevtools position="bottom-left" />
            <ReactQueryDevtools buttonPosition="bottom-right" />
          </>
        ),
      };
    })
  : () => null;

function RootComponent() {
  // Session-isolation listeners (pageshow bfcache guard + cross-tab logout)
  // are window-level and installed once for the app's lifetime here at the
  // router root, with teardown on unmount.
  useEffect(
    () =>
      installSessionIsolation({
        clearCache: () => queryClient.clear(),
        redirectToLogin,
      }),
    [],
  );

  return (
    <Providers>
      <Outlet />
      <Suspense fallback={null}>
        <Devtools />
      </Suspense>
    </Providers>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  errorComponent: RouteError,
  notFoundComponent: NotFound,
  pendingComponent: RoutePending,
});
